import React, { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faList } from '@fortawesome/free-solid-svg-icons';
import {
  fetchPracticeProviders,
  invalidatePracticeProvidersCache,
  fetchPracticePatients
} from '../../../store/slices/practiceSlice';
import { deleteProvider } from '../../../store/slices/providerSlice';
import { toast } from 'react-toastify';
import Table from '../../../components/Common/Table/Table';
import StatusBadge from '../../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import SearchBar from '../../../components/Common/SearchBar/SearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import ReassignmentModal from '../../../components/Common/ReassignmentModal/ReassignmentModal';
import './ProviderManagement.css';
import { buildDetailAddressLines, resolveStateDisplay } from '../../../utils/addressDisplay';
import { formatDisplayError } from '../../../utils/passwordValidation';
import { formatPhoneForDisplay } from '../../../utils/phoneUtils';
import { appendFromDashboardQuery } from '../../../utils/practiceNavigation';
import { canCreateProvider, canEditProvider, canDeleteProvider } from '../../../utils/roleUtils';
import {
  batchReassignPatientsToProvider,
  buildEmptyReassignments,
  buildReassignmentsForAll,
  fetchActiveProviders,
  fetchPatientsForStaff,
  getProviderAssigneeName,
  hasUnassignedReassignments
} from '../../../utils/staffReassignmentFlow';
import AddProviderModal from './AddProvider/AddProviderModal';
import { STAFF_LIST_TABLE_CARD, staffCol } from '../PracticeDashboard/staffListTableLayout';
import { matchesWordStartAny } from '../../../utils/searchMatch';

const ProviderManagement = ({ practiceId, isReadOnly = false, onOpenEventLogs = null, hideEventLogsButton = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    providersByPracticeId = {},
    practiceProvidersFetched = {},
    listLoading = {},
    error
  } = useSelector((state) => state.practice ?? {});
  const { user } = useSelector(state => state.auth);
  const allowCreate = !isReadOnly && canCreateProvider(user);
  const allowEdit = !isReadOnly && canEditProvider(user);
  const allowDelete = !isReadOnly && canDeleteProvider(user);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState(null);
  const [showDeleteReassignmentModal, setShowDeleteReassignmentModal] = useState(false);
  const [deleteAssignedPatients, setDeleteAssignedPatients] = useState([]);
  const [deleteAlternativeProviders, setDeleteAlternativeProviders] = useState([]);
  const [deleteReassignments, setDeleteReassignments] = useState({});
  const [isDeleteReassigning, setIsDeleteReassigning] = useState(false);
  const [isDeleteProcessing, setIsDeleteProcessing] = useState(false);
  const [sortBy, setSortBy] = useState('status');
  const [sortOrder, setSortOrder] = useState('asc');
  
  const [showAddProviderModal, setShowAddProviderModal] = useState(false);
  const [providerToEdit, setProviderToEdit] = useState(null);
  const [isViewProviderModal, setIsViewProviderModal] = useState(false);

  const pid = practiceId != null ? String(practiceId) : '';
  const hasCache = pid && practiceProvidersFetched[pid];
  const practiceProviders = pid ? providersByPracticeId[pid] ?? [] : [];

  useEffect(() => {
    if (practiceId && !practiceProvidersFetched[String(practiceId)]) {
      dispatch(fetchPracticeProviders(practiceId));
    }
  }, [practiceId, dispatch, practiceProvidersFetched]);

  // Debounce search term for real-time filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        setSearchTerm(localSearchTerm);
        setCurrentPage(1);
      }
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [localSearchTerm, searchTerm]);

  const filteredProviders = useMemo(() => {
    // Filter and sort providers based on search term and sort settings
    if (!practiceProviders || practiceProviders.length === 0) return [];

    let filtered = practiceProviders.filter(provider =>
      matchesWordStartAny(
        [provider.name, provider.email, provider.home_phone, provider.cell_phone],
        searchTerm
      )
    );
    
    if (statusFilter !== 'All') {
      filtered = filtered.filter(p => providerStatus(p.status) === statusFilter);
    }

    // Sort providers
    return [...filtered].sort((a, b) => {
      if (sortBy === 'idx') {
        const ai = Number(a.id) || 0;
        const bi = Number(b.id) || 0;
        return sortOrder === 'asc' ? ai - bi : bi - ai;
      }
      if (sortBy === 'status') {
        const statusA = (a.status || 'Active').toLowerCase();
        const statusB = (b.status || 'Active').toLowerCase();

        // Custom sort: Active first, then Inactive
        const statusOrder = { 'active': 1, 'inactive': 2 };
        const orderA = statusOrder[statusA] || 3;
        const orderB = statusOrder[statusB] || 3;

        if (orderA !== orderB) {
          return sortOrder === 'asc' ? orderA - orderB : orderB - orderA;
        }
        
        // Secondary sort: Name alphabetically
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      } else if (sortBy === 'name') {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return sortOrder === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      } else if (sortBy === 'addr') {
        const valueA = (a.address?.address_line_1 || '').toLowerCase();
        const valueB = (b.address?.address_line_1 || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'home') {
        const valueA = String(a.home_phone || '').toLowerCase();
        const valueB = String(b.home_phone || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'cell') {
        const valueA = String(a.cell_phone || '').toLowerCase();
        const valueB = String(b.cell_phone || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'fax') {
        const valueA = String(a.fax || '').toLowerCase();
        const valueB = String(b.fax || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'email') {
        const valueA = String(a.email || '').toLowerCase();
        const valueB = String(b.email || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else {
        const valueA = a[sortBy] || '';
        const valueB = b[sortBy] || '';
        return sortOrder === 'asc'
          ? String(valueA).localeCompare(String(valueB))
          : String(valueB).localeCompare(String(valueA));
      }
    });
  }, [searchTerm, practiceProviders, sortBy, sortOrder, statusFilter]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Calculate pagination
  const entriesPerPageNum = entriesPerPage === 'all' ? filteredProviders.length : parseInt(entriesPerPage);
  const totalPages = entriesPerPage === 'all' ? 1 : Math.ceil(filteredProviders.length / entriesPerPageNum);
  const startIndex = (currentPage - 1) * entriesPerPageNum;
  const endIndex = startIndex + entriesPerPageNum;
  const currentProviders = entriesPerPage === 'all' ? filteredProviders : filteredProviders.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  function providerStatus(s) {
    const v = (s || 'Active').toString().toLowerCase();
    return v === 'active' ? 'Active' : 'Inactive';
  }

  const getSafeValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string' && value.trim() === '') return 'N/A';
    return value;
  };

  const getDisplayName = (provider) => {
    const first = provider?.first_name || '';
    const last = provider?.last_name || '';
    if (last && first) return `${last}, ${first}`;
    return (
      provider?.name ||
      `${first} ${last}`.trim() ||
      'N/A'
    );
  };

  const formatAddressLines = (entity) =>
    buildDetailAddressLines({
      line1: entity.address?.address_line_1 ?? entity.address_line_1,
      line2: entity.address?.address_line_2 ?? entity.address_line_2,
      city: entity.address?.city ?? entity.city,
      stateDisplay: resolveStateDisplay(entity),
      zip: entity.address?.zip_code ?? entity.zip_code ?? entity.zipCode
    });


  const getUsername = (entity) =>
    entity?.username ||
    entity?.user?.username ||
    entity?.login_username ||
    'N/A';

  const getTwoWayAuth = (entity) => {
    const status =
      entity?.is_two_way_auth ??
      entity?.two_way_authentication_enabled ??
      entity?.twoWayAuth ??
      entity?.two_way_auth ??
      entity?.two_way_authentication ??
      entity?.two_way_authentication_status;

    if (status === 1 || status === true || status === 'Enabled') return 'Enabled';
    if (status === 0 || status === false || status === 'Disabled') return 'Disabled';

    return 'N/A';
  };

  const handleViewProvider = (provider) => {
    setProviderToEdit(provider.id);
    setIsViewProviderModal(true);
    setShowAddProviderModal(true);
  };

  const handleEditProvider = (providerId) => {
    setProviderToEdit(providerId);
    setIsViewProviderModal(false);
    setShowAddProviderModal(true);
  };


  const handleDeleteProvider = (provider) => {
    setProviderToDelete(provider);
    setShowDeleteModal(true);
  };

  const performProviderDelete = async (idToDelete) => {
    setShowDeleteModal(false);
    setShowDeleteReassignmentModal(false);
    setProviderToDelete(null);
    setDeleteAssignedPatients([]);
    setDeleteAlternativeProviders([]);
    setDeleteReassignments({});
    toast.success('Provider deleted successfully');
    try {
      await dispatch(deleteProvider({ practiceId, providerId: idToDelete })).unwrap();
      dispatch(fetchPracticePatients({ id: practiceId, params: { page: 1, limit: 1000 } }));
    } catch (error) {
      console.error('Error deleting provider:', error);
      if (error && (error.includes('already been deleted') || error.response?.status === 404 || error.message?.includes('not found'))) {
        toast.info('Provider not found or already deleted');
      } else {
        toast.error('Failed to delete provider');
      }

      dispatch(invalidatePracticeProvidersCache(practiceId));
      await dispatch(fetchPracticeProviders(practiceId));
    }
  };

  const startProviderDeleteFlow = async () => {
    if (!providerToDelete) return;

    const idToDelete = providerToDelete.id;
    setShowDeleteModal(false);
    setIsDeleteProcessing(true);

    try {
      const patients = await fetchPatientsForStaff(practiceId, { providerId: idToDelete });
      const alternatives = await fetchActiveProviders(practiceId, idToDelete);

      setDeleteAssignedPatients(patients);
      setDeleteAlternativeProviders(alternatives);

      if (patients.length > 0) {
        setDeleteReassignments(buildEmptyReassignments(patients));
        setShowDeleteReassignmentModal(true);
      } else {
        await performProviderDelete(idToDelete);
      }
    } catch (error) {
      console.error('Failed to start provider delete flow:', error);
      toast.error('Failed to check assigned patients.');
      setProviderToDelete(null);
    } finally {
      setIsDeleteProcessing(false);
    }
  };

  const handleProviderReassignAndDelete = async () => {
    if (!providerToDelete) return;

    if (hasUnassignedReassignments(deleteAssignedPatients, deleteReassignments)) {
      toast.warning('Please select a new provider for all patients.');
      return;
    }

    const idToDelete = providerToDelete.id;
    setIsDeleteReassigning(true);
    try {
      await batchReassignPatientsToProvider(practiceId, deleteReassignments);
      await performProviderDelete(idToDelete);
    } catch (error) {
      console.error('Provider reassignment before delete failed:', error);
      toast.error('Failed to reassign patients.');
    } finally {
      setIsDeleteReassigning(false);
    }
  };

  const cancelProviderDeleteReassignment = () => {
    setShowDeleteReassignmentModal(false);
    setProviderToDelete(null);
    setDeleteAssignedPatients([]);
    setDeleteAlternativeProviders([]);
    setDeleteReassignments({});
  };

  const confirmDelete = startProviderDeleteFlow;

  const cancelDelete = () => {
    if (isDeleteProcessing || isDeleteReassigning) return;
    setShowDeleteModal(false);
    setProviderToDelete(null);
  };

  const handleAddProvider = () => {
    setProviderToEdit(null);
    setIsViewProviderModal(false);
    setShowAddProviderModal(true);
  };


  const showLoading = practiceId && !hasCache && listLoading.providers;

  const handleSort = useCallback(
    (key) => {
      if (sortBy === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      else {
        setSortBy(key);
        setSortOrder('asc');
      }
    },
    [sortBy]
  );

  const columns = useMemo(
    () => [
      staffCol({
        key: 'idx',
        title: '#',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (_, i) => <span>{startIndex + i + 1}</span>
      }, 'idx', true),
      staffCol({
        key: 'status',
        title: 'Status',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (row) => <StatusBadge status={providerStatus(row.status)} />
      }, 'status', true),
      staffCol({
        key: 'name',
        title: 'Name',
        sortable: true,
        render: (row) => (
          <a
            href="#"
            className="pm-name-link"
            onClick={(e) => {
              e.preventDefault();
              handleViewProvider(row);
            }}
          >
            {getDisplayName(row)}
          </a>
        )
      }, 'name', true),
      staffCol({ 
        key: 'addr', 
        title: 'Address', 
        sortable: true, 
        render: (row) => {
          const addr = row.address?.address_line_1 || 'N/A';
          return (
            <div 
              style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} 
              title={addr}
            >
              {addr}
            </div>
          );
        }
      }, 'address', true),
      staffCol({ key: 'home', title: 'Home Phone', sortable: true, render: (row) => formatPhoneForDisplay(row.home_phone, 'N/A') }, 'homePhone', true),
      staffCol({ key: 'cell', title: 'Cell Phone', sortable: true, render: (row) => formatPhoneForDisplay(row.cell_phone, 'N/A') }, 'cellPhone', true),
      staffCol({ key: 'fax', title: 'Fax', sortable: true, render: (row) => formatPhoneForDisplay(row.fax, 'N/A') }, 'fax', true),
      staffCol({ key: 'email', title: 'Email', sortable: true, render: (row) => row.email || 'N/A' }, 'email', true),
      staffCol({
        key: 'action',
        title: 'Action',
        className: 'reusable-table-centered-col',
        render: (row) => (
          <ActionButtons
            onView={() => handleViewProvider(row)}
            onEdit={allowEdit ? () => handleEditProvider(row.id) : null}
            onDelete={allowDelete ? () => handleDeleteProvider(row) : null}
          />
        )
      }, 'action', true)
    ],
    [startIndex, practiceId, dispatch, allowEdit, allowDelete]
  );

  if (error && !showLoading) {
    return (
      <div className="alert alert-danger" role="alert">
        {formatDisplayError(error)}
      </div>
    );
  }

  return (
    <div className="pm-container">
      <div className="pm-header-row">
        <div className="pm-header-actions">
          <SearchBar 
            value={localSearchTerm} 
            onChange={setLocalSearchTerm} 
            className="pm-search-wrapper" 
            placeholder="Search providers..."
            label={null}
          />
          
          <div className="pm-filter-group">
            <select
              className="pm-status-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {allowCreate && (
            <button type="button" className="pm-add-btn" onClick={handleAddProvider}>
              <FontAwesomeIcon icon={faPlus} className="pm-add-icon" />
              Add Providers
            </button>
          )}
        </div>
      </div>

      <div className={STAFF_LIST_TABLE_CARD}>
        <Table
          columns={columns}
          data={currentProviders}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          isLoading={showLoading}
          loadingMode="skeleton"
          skeletonRows={10}
          emptyMessage="No providers found"
        />

        <ListScreenPaginationBar
          entriesValue={entriesPerPage}
          onEntriesChange={handleEntriesChange}
          currentPage={currentPage}
          totalRecords={filteredProviders.length}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          leftSlot={!hideEventLogsButton && (
            <button
              type="button"
              className="pm-events-log-btn"
              onClick={() => {
                if (typeof onOpenEventLogs === 'function') {
                  onOpenEventLogs();
                  return;
                }
                navigate(appendFromDashboardQuery(`/event-logs/Provider/all?practiceId=${practiceId}`, location));
              }}
            >
              <FontAwesomeIcon icon={faList} className="pm-add-icon" />
              Event Logs
            </button>
          )}
        />
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button
                type="button"
                className="modal-close"
                onClick={cancelDelete}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the provider{' '}
                <strong>{providerToDelete?.name}</strong>?
              </p>
              <p className="modal-warning">
                Assigned patients must be reassigned before this provider can be deleted.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={isDeleteProcessing}
              >
                {isDeleteProcessing ? 'Checking patients...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ReassignmentModal
        show={showDeleteReassignmentModal}
        onClose={cancelProviderDeleteReassignment}
        title="Reassign Patients"
        description={`Select a new provider for each of the ${deleteAssignedPatients.length} patient(s) before deleting ${providerToDelete?.name || 'this provider'}.`}
        patients={deleteAssignedPatients}
        alternatives={deleteAlternativeProviders}
        reassignments={deleteReassignments}
        onReassignmentChange={(patientId, providerId) =>
          setDeleteReassignments((prev) => ({ ...prev, [patientId]: providerId }))
        }
        onAssignAll={(providerId) =>
          setDeleteReassignments(buildReassignmentsForAll(deleteAssignedPatients, providerId))
        }
        onSave={handleProviderReassignAndDelete}
        isSaving={isDeleteReassigning}
        saveLabel="Reassign & Delete"
        optionPlaceholder="Select provider..."
        currentLabel="Current provider"
        reassignToLabel="Reassign to provider"
        getCurrentAssignee={getProviderAssigneeName}
      />

      {showAddProviderModal && (
        <AddProviderModal
          isOpen={showAddProviderModal}
          onClose={() => {
            setShowAddProviderModal(false);
            setProviderToEdit(null);
            setIsViewProviderModal(false);
          }}
          practiceId={practiceId}
          isViewMode={isViewProviderModal}
          isEditMode={!!providerToEdit && !isViewProviderModal}
          editId={providerToEdit}
        />
      )}
    </div>
  );
};

export default ProviderManagement;

const DetailRow = ({ label, value, multiline = false }) => (
  <div className="detail-row">
    <span className="detail-label">{label}</span>
    <span className="detail-separator">:</span>
    <span className={`detail-value ${multiline ? 'detail-value--multiline' : ''}`}>
      {Array.isArray(value) ? (
        value.map((line, index) => (
          <Fragment key={`${label}-${index}`}>
            {line || 'N/A'}
            {index < value.length - 1 && <br />}
          </Fragment>
        ))
      ) : (
        value || 'N/A'
      )}
    </span>
  </div>
);